import type { BackupInfo, Card, CloudStatus, Dashboard, DecisionStats, DrillResult, DrillResultInput, ErrorType, Goal, GoalInput, ItemDetail, ItemInput, ItemSummary, MemoryStats, MetricInfo, NextResponse, Prediction, PredictionDetail, PredictionInput, ResolveInput, Settings, SubmitResponse, TrainingPlan, TrainingStats } from './types';
import { engineRequest, isDevice } from './engine/client';

const FIELD_LABEL: Record<string, string> = {
  title: '개념', question: '질문', probability: '확률', resolve_by: '마감일', note: '수정 이유',
  prompt: '질문', answer: '답', target_value: '목표값', base_rate: '기준율',
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function detailText(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { loc?: (string | number)[]; msg?: string };
    const field = first.loc ? String(first.loc[first.loc.length - 1]) : '';
    return `${FIELD_LABEL[field] ?? field}: ${first.msg ?? '값이 올바르지 않습니다'}`;
  }
  return undefined;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (isDevice) return deviceRequest<T>(method, path, body);
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, '서버에 연결할 수 없습니다. APEX 서버(run.ps1)가 켜져 있는지 확인하세요.');
  }
  if (!res.ok) {
    let message = `요청 실패 (${res.status})`;
    try {
      message = detailText((await res.json()).detail) ?? message;
    } catch {
      // 본문이 JSON 이 아님
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

/** 기기 모드: 같은 API 를 기기 안 엔진이 처리한다 (인터넷·PC 없이 동작). */
async function deviceRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res;
  try {
    res = await engineRequest(method, path, body === undefined ? null : JSON.stringify(body));
  } catch (e) {
    throw new ApiError(0, `기기 엔진 오류: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (res.status >= 400) {
    let message = `요청 실패 (${res.status})`;
    try {
      message = detailText(JSON.parse(res.text).detail) ?? message;
    } catch {
      // 본문이 JSON 이 아님
    }
    throw new ApiError(res.status, message);
  }
  return JSON.parse(res.text) as T;
}

function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

function query(params: Record<string, string | boolean | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== false) p.set(k, String(v));
  }
  return p.toString();
}

export const api = {
  dashboard: () => get<Dashboard>('/api/dashboard'),
  memoryStats: () => get<MemoryStats>('/api/stats/memory'),
  decisionStats: () => get<DecisionStats>('/api/stats/decisions'),

  items: (p: { q?: string; domain?: string; archived?: boolean; sort?: string }) => get<ItemSummary[]>(`/api/knowledge?${query(p)}`),
  domains: () => get<{ domain: string; count: number }[]>('/api/knowledge/domains'),
  titles: () => get<{ id: number; title: string }[]>('/api/knowledge/titles'),
  item: (id: number) => get<ItemDetail>(`/api/knowledge/${id}`),
  createItem: (body: ItemInput) => request<ItemDetail>('POST', '/api/knowledge', body),
  updateItem: (id: number, body: ItemInput) => request<ItemDetail>('PUT', `/api/knowledge/${id}`, body),
  archiveItem: (id: number, archived: boolean) => request<ItemDetail>('POST', `/api/knowledge/${id}/archive`, { archived }),
  deleteItem: (id: number) => request<{ ok: true }>('DELETE', `/api/knowledge/${id}`),
  addCard: (id: number, prompt: string, answer: string) => request<ItemDetail>('POST', `/api/knowledge/${id}/cards`, { prompt, answer }),
  patchCard: (id: number, body: { prompt?: string; answer?: string; suspended?: boolean }) => request<Card>('PATCH', `/api/cards/${id}`, body),
  deleteCard: (id: number) => request<{ ok: true }>('DELETE', `/api/cards/${id}`),

  next: () => get<NextResponse>('/api/review/next'),
  submit: (cardId: number, body: { rating: number; typed_answer: string; duration_ms: number; error_type: ErrorType | null }) =>
    request<SubmitResponse>('POST', `/api/review/${cardId}`, body),
  undo: (reviewId: number) => request<{ ok: true; card_id: number }>('POST', '/api/review/undo', { review_id: reviewId }),

  predictions: (status: string) => get<Prediction[]>(`/api/predictions?status=${status}`),
  prediction: (id: number) => get<PredictionDetail>(`/api/predictions/${id}`),
  createPrediction: (body: PredictionInput) => request<PredictionDetail>('POST', '/api/predictions', body),
  updateProbability: (id: number, probability: number, note: string) =>
    request<PredictionDetail>('POST', `/api/predictions/${id}/update`, { probability, note }),
  resolve: (id: number, body: ResolveInput) => request<PredictionDetail>('POST', `/api/predictions/${id}/resolve`, body),
  deletePrediction: (id: number) => request<{ ok: true }>('DELETE', `/api/predictions/${id}`),

  goals: () => get<Goal[]>('/api/goals'),
  metrics: () => get<MetricInfo[]>('/api/goals/metrics'),
  createGoal: (body: GoalInput) => request<Goal>('POST', '/api/goals', body),
  updateGoal: (id: number, body: GoalInput) => request<Goal>('PUT', `/api/goals/${id}`, body),
  deleteGoal: (id: number) => request<{ ok: true }>('DELETE', `/api/goals/${id}`),

  trainingToday: () => get<TrainingPlan>('/api/training/today'),
  trainingSubmit: (body: DrillResultInput) => request<DrillResult>('POST', '/api/training/results', body),
  trainingStats: () => get<TrainingStats>('/api/training/stats'),

  settings: () => get<Settings>('/api/settings'),
  cloudStatus: () => get<CloudStatus>('/api/cloud/local/status'),
  cloudLink: (body: { url: string; token: string; code: string }) => request<CloudStatus>('POST', '/api/cloud/local/link', body),
  cloudSync: () => request<CloudStatus>('POST', '/api/cloud/local/sync'),
  cloudUnlink: () => request<CloudStatus>('POST', '/api/cloud/local/unlink'),
  saveSettings: (body: Partial<Settings>) => request<Settings>('PUT', '/api/settings', body),
  backups: () => get<BackupInfo[]>('/api/backups'),
  backup: () => request<BackupInfo>('POST', '/api/backup'),
  importJson: (data: unknown) =>
    request<{ ok: true; counts: Record<string, number>; backup: string }>('POST', '/api/import/json?confirm=REPLACE', data),
};
